// ═══════════════════════════════════════════════════════════════════════════════
// AuthGuard — Azure Static Web Apps built-in authentication gate
//
// Uses SWA's /.auth/me endpoint to check sign-in status.
//   - Authenticated → syncs user into AppState, renders children
//   - Unauthenticated → redirects to /.auth/login/aad (SWA handles OAuth
//     server-side with client_secret, avoiding the PKCE/SPA platform mismatch
//     that breaks MSAL redirect flow)
//   - Not on SWA (local dev) → shows a warning banner, renders children
//
// SCOPE NOTE: this gate covers the APP SHELL only. It does NOT authenticate
// API calls. PR #630 removed x-ms-client-principal as an authentication
// source, so an SWA session alone no longer opens any staff-gated endpoint -
// /api calls need an MSAL bearer, which src/lib/auth/bearerBridge.ts obtains
// by using the SWA identity here purely as an ssoSilent loginHint.
//
// The PKCE/SPA caveat above is the reason interactive MSAL sign-in is a LAST
// resort in that bridge rather than the primary path, and it is why whether
// the app registration behind VITE_AZURE_CLIENT_ID carries SPA-platform
// redirect URIs is load-bearing. That remains unverified from the repo.
// ═══════════════════════════════════════════════════════════════════════════════
import { ReactNode, useEffect, useState, CSSProperties } from 'react';
import { useApp } from '../hooks/useAppState';
import type { User } from '../types';
import { CustomerHoldingPage } from './CustomerHoldingPage';
import { apiFetch } from '../services/apiFetch';
import { getBearer, startInteractiveAuth, noteBearerAcquired } from '../lib/auth/msalConfig';
import { loadAuthenticatedProfile } from '../lib/auth/profileGate';

// Re-export for consumers that need it directly (api.ts lazy-imports this)
export { getAccessToken } from '../lib/auth/msalConfig';

// ─── SWA client principal types ──────────────────────────────────────────────

interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims: Array<{ typ: string; val: string }>;
}

type AuthState = 'loading' | 'authenticated' | 'customer' | 'unauthenticated' | 'no-swa' | 'error';

// ─── Public entry-point ───────────────────────────────────────────────────────

export function AuthGuard({ children }: { children: ReactNode }) {
  const { dispatch } = useApp();
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [customerProfile, setCustomerProfile] = useState<{ pending: boolean; name?: string } | null>(null);

  // Check SWA auth status on mount.
  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const res = await fetch('/.auth/me');
        // Vite dev server returns index.html (not JSON) for unknown routes.
        // If JSON parse fails, we're not on SWA.
        let data: { clientPrincipal: SwaClientPrincipal | null };
        try {
          data = await res.json();
        } catch {
          // Non-JSON from /.auth/me means the SWA auth platform isn't in front
          // of us. In local dev that's expected (plain `vite` has no /.auth) —
          // show the dev banner and render the app. In a deployed build it is
          // never safe to render the app without a verified principal, so we
          // treat it as unauthenticated and let the redirect effect take over.
          // This prevents the menu from ever flashing before sign-in.
          if (!cancelled) setAuthState(import.meta.env.DEV ? 'no-swa' : 'unauthenticated');
          return;
        }

        if (cancelled) return;

        if (!data.clientPrincipal) {
          setAuthState('unauthenticated');
          return;
        }

        // Extract user info from SWA claims.
        const p = data.clientPrincipal;
        const claim = (type: string) =>
          p.claims?.find(c => c.typ === type || c.typ.endsWith('/' + type))?.val || '';

        const baseUser: User = {
          id: claim('objectidentifier') || p.userId,
          name: claim('name') || p.userDetails || 'Unknown User',
          email: p.userDetails || claim('emailaddress') || '',
          avatar: '👤',
          shift: 'Shift 1',
          tier: 'T1',
          role: 'analyst',
          entraOid: claim('objectidentifier') || p.userId,
        };

        // SWA identifies the login hint only. The bearer-authenticated profile
        // decides which shell to render; failed profile checks keep it covered.
        {
          try {
            const result = await loadAuthenticatedProfile({
              getBearer,
              fetchProfile: token => apiFetch('/api/me', {
                headers: { Authorization: `Bearer ${token}` },
              }),
              startInteractiveAuth,
              isCancelled: () => cancelled,
            });
            if (cancelled || result.kind === 'cancelled') return;
            if (result.kind !== 'profile') {
              if (result.kind === 'error') setAuthState('error');
              return;
            }
            {
              const profile = await result.response.json();
              if (cancelled) return;
              if (!profile || typeof profile.id !== 'string' || !profile.id.trim()
                || !['admin', 'lead', 'analyst', 'customer'].includes(profile.role)) {
                setAuthState('error');
                return;
              }
              noteBearerAcquired();
              dispatch({
                type: 'SET_CURRENT_USER',
                user: {
                  ...baseUser,
                  // Use the Cosmos profile id (Entra OID) to ensure RBAC
                  // assignment matching works even if the SWA claim gave a
                  // different identifier format.
                  id: profile.id || baseUser.id,
                  entraOid: profile.id || baseUser.entraOid,
                  role: profile.role,
                  tier: profile.tier || baseUser.tier,
                  shift: profile.shift || baseUser.shift,
                  name: profile.displayName || baseUser.name,
                  email: profile.email || baseUser.email,
                  // Inline RBAC permissions resolved server-side (bypasses
                  // client-side userId matching issues)
                  rbacPermissions: profile.rbacPermissions || [],
                  customerSlugs: profile.customerSlugs || undefined,
                  pendingCustomerAccess: !!profile.pendingCustomerAccess,
                  customerPortalEnabled: !!profile.customerPortalEnabled,
                },
              });
              if (profile.role === 'customer') {
                const pending = !!profile.pendingCustomerAccess || !(profile.customerSlugs?.length);
                if (pending) {
                  // Unresolved guest — holding page until an admin approves.
                  setCustomerProfile({ pending: true, name: profile.displayName || baseUser.name });
                  setAuthState('customer');
                  return;
                }
                // Resolved customer — render the app shell. AppLayout hard-pins
                // customer-role users to the Customer Portal view, and the
                // sidebar/topbar hide every internal surface for this role.
              }
            }
          } catch {
            if (!cancelled) setAuthState('error');
            return;
          }
        }
        if (!cancelled) setAuthState('authenticated');
      } catch {
        // Network error fetching /.auth/me. Local dev → assume "not on SWA" and
        // render with the dev banner. Deployed → never expose the app; treat as
        // unauthenticated so the splash stays up and we redirect to login.
        if (!cancelled) setAuthState(import.meta.env.DEV ? 'no-swa' : 'unauthenticated');
      }
    }

    checkAuth();
    return () => { cancelled = true; };
  }, [dispatch]);

  // Redirect to SWA login when unauthenticated.
  useEffect(() => {
    if (authState === 'unauthenticated') {
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
    }
  }, [authState]);

  if (authState === 'no-swa') {
    return (
      <>
        <UnconfiguredBanner />
        {children}
      </>
    );
  }

  if (authState === 'error') {
    return <AuthErrorScreen onRetry={() => window.location.reload()} />;
  }

  // Customer-role users never see the internal app shell. Until the customer
  // portal view ships, both pending and resolved customers get the holding
  // page (pending decides the copy).
  if (authState === 'customer') {
    return <CustomerHoldingPage pending={customerProfile?.pending ?? true} name={customerProfile?.name} />;
  }

  // Every non-authenticated state (initial 'loading' AND 'unauthenticated'
  // while the login redirect fires) shows the same neutral splash. No sidebar,
  // nav, or page data is ever mounted until we have a verified principal, so
  // the app chrome can never flash before sign-in.
  if (authState !== 'authenticated') {
    return <AuthSplash />;
  }

  return <>{children}</>;
}

// ─── Banner shown in local dev (no SWA) ──────────────────────────────────────

function UnconfiguredBanner() {
  const bannerStyle: CSSProperties = {
    background: 'linear-gradient(90deg, #f59e0b, #d97706)',
    color: '#000',
    padding: '6px 16px',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'center',
    flexShrink: 0,
  };
  return (
    <div style={bannerStyle}>
      ⚠️ Authentication not available — run via{' '}
      <code style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,.12)', padding: '0 4px', borderRadius: 3 }}>
        swa start
      </code>{' '}
      or deploy to Azure Static Web Apps to enable sign-in
    </div>
  );
}

// ─── Error screen ────────────────────────────────────────────────────────────

function AuthErrorScreen({ onRetry }: { onRetry: () => void }) {
  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: 16,
    background: '#0f1117',
    color: '#94a3b8',
  };

  return (
    <div style={containerStyle}>
      <div style={{ fontSize: 40 }}>⚠️</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0' }}>Sign-in failed</div>
      <div style={{ fontSize: 13, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
        Could not verify authentication status. Please try again.
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: 8,
          padding: '8px 20px',
          background: '#3b82f6',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
}

// ─── Splash screen ───────────────────────────────────────────────────────────
//
// Neutral, full-viewport pre-auth splash: MXDR365 logo + spinner only. Shown
// for every non-authenticated state until SWA resolves sign-in. Deliberately
// contains zero app chrome (no sidebar, nav, or data) and zero account-specific
// text so nothing is exposed before login. Rendered as a fixed overlay covering
// the entire viewport with the app background, so it fully masks anything that
// might otherwise mount behind it.

function AuthSplash() {
  const containerStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 2147483647,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 28,
    background: '#080c14',
  };

  return (
    <div style={containerStyle} role="status" aria-label="Loading MXDR365" aria-busy="true">
      {/* Official stacked logo — reversed (light) color variant straight on
          the dark splash background, no plate. */}
      <img
        src="/brand/mxdr365-logo-stacked-light.png"
        alt="MXDR365 — we have the watch"
        width={180}
        style={{ display: 'block', height: 'auto' }}
      />
      <div style={{
        width: 32, height: 32,
        border: '3px solid rgba(148,163,184,0.25)',
        borderTopColor: '#4a8fe2',
        borderRadius: '50%',
        animation: 'auth-splash-spin 0.7s linear infinite',
      }} />
      <style>{`
        @keyframes auth-splash-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
